/* ============================================================================
   ROWMAN — patient interface
   Shopify supplies registration and purchase data.
   Editable profile details and the photograph remain private on this device.
   ============================================================================ */
(function () {
  'use strict';

  var R = window.ROWMAN;
  if (!R) return;

  var strings = R.strings || {};
  var PROFILE_PREFIX = 'rowman.patient.profile.';
  var MAX_PHOTO_BYTES = 4 * 1024 * 1024;

  var FIELDS = [
    'status',
    'protocol',
    'tier',
    'memberSince',
    'wellness',
    'journal',
    'collection',
    'purchase'
  ];

  var TAGS = {
    status: 'Active',
    protocol: 'Assigned',
    tier: 'Verified',
    memberSince: 'On record',
    wellness: 'Active',
    journal: 'Logged',
    collection: 'In progress',
    purchase: 'In progress'
  };

  function readState() {
    var el = document.getElementById('patient-data');

    var base = {
      enrolled: false,
      status: null,
      name: '',
      email: '',
      protocol: '',
      tier: '',
      memberSince: null,
      wellness: null,
      journal: null,
      collection: null,
      purchase: null,
      patientId: null,
      photo: ''
    };

    if (!el) return base;

    try {
      var data = JSON.parse(el.textContent);

      if (data && data.enrolled) {
        Object.keys(data).forEach(function (key) {
          base[key] = data[key];
        });
      }
    } catch (error) {
      console.warn('[ROWMAN] patient-data parse failed', error);
    }

    return base;
  }

  function profileKey(state) {
    return PROFILE_PREFIX + (state.patientId || state.email || 'guest');
  }

  function loadProfile(state) {
    try {
      return JSON.parse(localStorage.getItem(profileKey(state)) || 'null');
    } catch (error) {
      return null;
    }
  }

  function saveProfile(state, profile) {
    try {
      localStorage.setItem(profileKey(state), JSON.stringify(profile));
      return true;
    } catch (error) {
      console.warn('[ROWMAN] patient profile could not be saved', error);
      return false;
    }
  }

  function hasValue(value) {
    return value !== null && value !== undefined && value !== '';
  }

  function applyStoredProfile(state) {
    var saved = loadProfile(state);

    if (!saved) return state;

    if (hasValue(saved.name)) state.name = saved.name;
    if (hasValue(saved.protocol)) state.protocol = saved.protocol;
    if (hasValue(saved.tier)) state.tier = saved.tier;
    if (hasValue(saved.photo)) state.photo = saved.photo;

    return state;
  }

  function setInputValue(id, value) {
    var input = document.getElementById(id);
    if (input && hasValue(value)) input.value = value;
  }

  function renderPhoto(photo) {
    var frame = document.getElementById('patientPfp');
    if (!frame) return;

    if (photo) {
      frame.style.backgroundImage = 'url("' + photo + '")';
      frame.style.backgroundSize = 'cover';
      frame.style.backgroundPosition = 'center';
      frame.classList.add('has-photo');
      frame.setAttribute('data-photo', photo);
    } else {
      frame.style.backgroundImage = '';
      frame.classList.remove('has-photo');
      frame.removeAttribute('data-photo');
    }
  }

  function readProfileForm(state) {
    var nameInput = document.getElementById('pfName');
    var protocolInput = document.getElementById('pfProtocol');
    var tierInput = document.getElementById('pfTier');
    var photoFrame = document.getElementById('patientPfp');

    return {
      name: nameInput ? nameInput.value.trim() : state.name,
      protocol: protocolInput ? protocolInput.value : state.protocol,
      tier: tierInput ? tierInput.value : state.tier,
      photo: photoFrame ? photoFrame.getAttribute('data-photo') || '' : state.photo,
      updatedAt: new Date().toISOString()
    };
  }

  function resizePhoto(file, callback) {
    var reader = new FileReader();

    reader.onerror = function () {
      callback(null);
    };

    reader.onload = function (event) {
      var image = new Image();

      image.onerror = function () {
        callback(null);
      };

      image.onload = function () {
        var maximum = 900;
        var scale = Math.min(1, maximum / Math.max(image.width, image.height));
        var width = Math.max(1, Math.round(image.width * scale));
        var height = Math.max(1, Math.round(image.height * scale));
        var canvas = document.createElement('canvas');
        var context = canvas.getContext('2d');

        canvas.width = width;
        canvas.height = height;
        context.drawImage(image, 0, 0, width, height);

        callback(canvas.toDataURL('image/jpeg', 0.84));
      };

      image.src = event.target.result;
    };

    reader.readAsDataURL(file);
  }

  function renderPatient(state) {
    var enrolled = !!state.enrolled;
    var notEnrolled = strings.notEnrolled || 'Not enrolled';

    document.body.setAttribute(
      'data-patient-enrolled',
      enrolled ? 'true' : 'false'
    );

    FIELDS.forEach(function (key) {
      var value = state[key];
      var available = hasValue(value);

      document
        .querySelectorAll('[data-field="' + key + '"]')
        .forEach(function (element) {
          element.textContent = available ? value : 'N/A';
        });

      document
        .querySelectorAll('[data-tag="' + key + '"]')
        .forEach(function (element) {
          element.textContent =
            enrolled && available ? TAGS[key] : notEnrolled;
        });

      document
        .querySelectorAll('[data-dot="' + key + '"]')
        .forEach(function (element) {
          element.className =
            'status-dot ' +
            (enrolled && available ? 'active' : 'monitoring');

          element.setAttribute('data-dot', key);
        });
    });

    document.querySelectorAll('[data-ecg-status]').forEach(function (element) {
      element.textContent = enrolled
        ? strings.ecgActive || 'MONITORING · PATIENT ACTIVE'
        : strings.ecgAwaiting || 'MONITORING · AWAITING ENROLLMENT';
    });

    document.querySelectorAll('[data-radar]').forEach(function (element) {
      element.setAttribute(
        'data-wellness',
        enrolled && hasValue(state.wellness) ? state.wellness : 0
      );
    });

    var barMaximums = {
      wellness: 100,
      collection: 100
    };

    Object.keys(barMaximums).forEach(function (key) {
      var rawValue = parseFloat(state[key]);

      document
        .querySelectorAll('[data-bar="' + key + '"]')
        .forEach(function (bar) {
          if (enrolled && !isNaN(rawValue)) {
            var percentage =
              (rawValue / barMaximums[key]) * 100;

            bar.style.width =
              Math.max(0, Math.min(100, percentage)) + '%';

            bar.removeAttribute('data-indeterminate');
          } else {
            bar.style.width = '';
            bar.setAttribute('data-indeterminate', 'true');
          }
        });
    });

    document.querySelectorAll('[data-patient-id]').forEach(function (element) {
      element.textContent = state.patientId || 'ROW-GUEST';
    });

    setInputValue('pfName', state.name);
    setInputValue('pfProtocol', state.protocol);
    setInputValue('pfTier', state.tier);
    renderPhoto(state.photo);

    document.dispatchEvent(
      new CustomEvent('rowman:patient:render', {
        detail: state
      })
    );
  }

  function bindProfileControls(state) {
    if (!state.enrolled) return function () {};

    var frame = document.getElementById('patientPfp');
    var fileInput = document.getElementById('patientPfpInput');
    var saveButton = document.querySelector('[data-idcard-save]');
    var cleanup = [];

    function openPhotoPicker(event) {
      if (event && event.type === 'keydown') {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
      }

      if (fileInput) fileInput.click();
    }

    if (frame && fileInput && frame.getAttribute('data-patient-bound') !== 'true') {
      frame.setAttribute('data-patient-bound', 'true');
      frame.setAttribute('role', 'button');
      frame.setAttribute('tabindex', '0');
      frame.setAttribute('aria-label', 'Choose patient photograph');

      frame.addEventListener('click', openPhotoPicker);
      frame.addEventListener('keydown', openPhotoPicker);

      cleanup.push(function () {
        frame.removeEventListener('click', openPhotoPicker);
        frame.removeEventListener('keydown', openPhotoPicker);
      });
    }

    function onPhotoChange() {
      var file = fileInput && fileInput.files
        ? fileInput.files[0]
        : null;

      if (!file) return;

      if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
        if (R.toast) R.toast('Choose a JPG, PNG, or WEBP image');
        fileInput.value = '';
        return;
      }

      if (file.size > MAX_PHOTO_BYTES) {
        if (R.toast) R.toast('Photograph must be smaller than 4MB');
        fileInput.value = '';
        return;
      }

      resizePhoto(file, function (photo) {
        if (!photo) {
          if (R.toast) R.toast('That photograph could not be read');
          return;
        }

        state.photo = photo;
        renderPhoto(photo);

        var profile = readProfileForm(state);
        saveProfile(state, profile);

        if (R.toast) R.toast('Photograph updated');
      });
    }

    if (fileInput) {
      fileInput.addEventListener('change', onPhotoChange);

      cleanup.push(function () {
        fileInput.removeEventListener('change', onPhotoChange);
      });
    }

    function onSave(event) {
      event.preventDefault();

      var profile = readProfileForm(state);
      var saved = saveProfile(state, profile);

      if (saved) {
        state.name = profile.name;
        state.protocol = profile.protocol;
        state.tier = profile.tier;
        state.photo = profile.photo;

        renderPatient(state);

        if (R.toast) R.toast('Patient profile saved');
      } else if (R.toast) {
        R.toast('Patient profile could not be saved');
      }
    }

    if (saveButton) {
      saveButton.addEventListener('click', onSave);

      cleanup.push(function () {
        saveButton.removeEventListener('click', onSave);
      });
    }

    return function () {
      cleanup.forEach(function (removeListener) {
        removeListener();
      });
    };
  }

  R.register({
    name: 'PatientInterface',
    scope: null,

    mount: function () {
      var state = applyStoredProfile(readState());

      renderPatient(state);

      return bindProfileControls(state);
    }
  });
}());
